/**
 * The one place the dashboard reads `me.get`.
 *
 * Every shell surface needs the same snapshot — identity, active org,
 * memberships, units, permissions — and reading it through one hook keeps the
 * query key, the suspense boundary and the derived types in a single file
 * instead of fifteen.
 *
 * ⚠️ `useSuspenseQuery` requires the route to have prefetched `me.get` in its
 * `loader` (`ensureQueryData`). Without that, suspending mid-navigation
 * remounts the entire app. `/_dashboard`'s `beforeLoad` already does it for
 * every child route.
 */
import type { RouterOutputs } from "@gately/api";
import { useSuspenseQuery } from "@tanstack/react-query";

import { orpc } from "~/lib/orpc";

export type Me = RouterOutputs["me"]["get"];
/** One membership row: an org the user belongs to, with that org's own type. */
export type Membership = Me["memberships"][number];
/** A unit the user belongs to in the ACTIVE org — their company or foyer. */
export type ActiveUnit = Me["activeUnits"][number];

export function useMe(): Me {
  const { data } = useSuspenseQuery(orpc.me.get.queryOptions());
  return data;
}

/** The caller's membership row for the active org, if any. */
export function activeMembership(me: Me): Membership | undefined {
  return me.memberships.find((m) => m.orgId === me.activeOrganizationId);
}

/**
 * The active organization's IANA time zone — the clock every visit hour is
 * read on.
 *
 * Throws rather than substituting a zone when no org is active. Every caller
 * sits under `/_dashboard` or `/poste`, which cannot render without one: their
 * org-scoped queries all go through `memberProcedure`, which rejects a session
 * with no active org long before this runs. The alternatives — UTC, or the
 * viewer's own device zone — both silently print hours the gate does not
 * enforce, which is exactly the failure this refactor removes (GATELY-62).
 */
export function orgTimeZone(me: Me): string {
  const tz = me.activeOrg?.timezone;
  if (!tz) {
    throw new Error(
      "Aucune organisation active : fuseau horaire indisponible.",
    );
  }
  return tz;
}

export function useOrgTimeZone(): string {
  return orgTimeZone(useMe());
}

/**
 * The unit that represents the user in the active org. Server order is
 * owned-first, so the first row is the one to show; the rest live behind the
 * org menu (see `deriveIdentity`).
 */
export function primaryUnit(me: Me): ActiveUnit | null {
  return me.activeUnits[0] ?? null;
}
